import React, { useState } from 'react';
import styled from 'styled-components';
import StarRating from '../reviews/starRating';
import { Typography, Grid, Box, Button, Collapse } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Icon from '../../utils/icon';
import qt from '../../images/QIT_CSL_badge_2024.jpg';
import Gold3Stars from '../../images/qt-sweetcombe-marque-3-star-gold-rgb.png';
import Orange3Stars from '../../images/qt-sweetcombe-marque-3-star-orange-rgb.png';
import Silver3Stars from '../../images/qt-sweetcombe-marque-3-star-silver-rgb.png';
import Gold4Stars from '../../images/qt-sweetcombe-marque-4-star-gold-rgb.png';
import Orange4Stars from '../../images/qt-sweetcombe-marque-4-star-orange-rgb.png';
import Silver4Stars from '../../images/qt-sweetcombe-marque-4-star-silver-rgb.png';
import Gold5Stars from '../../images/qt-sweetcombe-marque-5-star-gold-rgb.png';
import Orange5Stars from '../../images/qt-sweetcombe-marque-5-star-orange-rgb.png';
import Silver5Stars from '../../images/qt-sweetcombe-marque-5-star-silver-rgb.png';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-column-gap: 1rem;

  img {
    max-width: 8rem;
    height: auto;
  }
  .svgWrapper.yes path {
    fill: #ffde00;
  }
  @media ${(props) => props.theme.mq.md} {
    grid-template-columns: repeat(12, 1fr);
  }
`;

const DatedImage = styled.div`
  grid-column: ${(props) => (props?.hasQitg?.length > 0 ? '5/5' : '1/1')};
  grid-row: 1 / auto;
  img {
    max-width: 8rem;
    height: auto;
  }
  @media ${(props) => props.theme.mq.md} {
    grid-column: ${(props) => (props?.hasQitg?.length > 0 ? 'auto/5' : '1/3')};
  }
`;

const RatingImageArea = styled.div`
  grid-column: 1/7;
  grid-row: 1 / auto;
  grid-template-columns: repeat(6, 1fr);
  display: grid;
  @media ${(props) => props.theme.mq.md} {
    grid-template-columns: repeat(12, 1fr);
    grid-column: 1/13;
  }
`;

const RatingImage = styled.div`
  grid-column: 1/3;
  grid-row: 1 / auto;
`;

const RatingKey = styled.div`
  grid-column: 1/13;
  grid-row: 3 / auto;
`;

const RatingKeyButton = styled.div`
  grid-column: 1/13;
  grid-row: 2 / auto;
`;

const StarRatingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  order: 0;
  .starRating {
    height: auto !important;
    align-items: center;
    justify-content: center;
  }
  @media ${(props) => props.theme.mq.md} {
    order: unset;
  }
`;

const Info = styled(Grid)`
  display: grid;
  grid-template-columns: 105px 1fr;
  grid-column-gap: 2rem;
  margin-bottom: 2rem;
  .starRating {
    grid-area: unset;
  }
`;

const Key = styled(Grid)`
  display: grid;
  grid-template-columns: 21px 1fr;
  grid-column-gap: 2rem;
  margin-bottom: 2rem;
  .starRating {
    grid-area: unset;
  }
`;

const IconWrapper = styled(Icon)`
  &.gold {
    path {
      fill: #978454;
    }
  }
  &.silver {
    path {
      fill: #a7a8a9;
    }
  }
  &.orange {
    path {
      fill: #fd5000;
    }
  }
`;

const PropertyGrading = ({ attrs, pros, cons }) => {
  const [open, setOpen] = useState(false);

  function veRating(attrs) {
    const gradings = attrs['Quality In Tourism Grading']
      ? attrs['Quality In Tourism Grading']
      : [];

    const renderImages = gradings.map((grade) => {
      const values = grade.name.split(' ');

      const imageMatrix = {
        gold3: Gold3Stars,
        orange3: Orange3Stars,
        silver3: Silver3Stars,
        gold4: Gold4Stars,
        orange4: Orange4Stars,
        silver4: Silver4Stars,
        gold5: Gold5Stars,
        orange5: Orange5Stars,
        silver5: Silver5Stars,
      };

      return (
        <React.Fragment key={grade.name}>
          <RatingImage>
            {values[2] && (
              <img
                className='qtg'
                alt={`Quality in tourism grade - ${grade.name}`}
                src={
                  imageMatrix[`${values[2].concat(values[0]).toLowerCase()}`]
                }
              />
            )}
          </RatingImage>
          <RatingKeyButton>
            <Button
              variant='text'
              color='secondary'
              onClick={() => setOpen(!open)}
              endIcon={<InfoIcon />}
            >
              Quality rating {cons}
            </Button>
          </RatingKeyButton>

          {/* Dated Image needs to be positioned here */}

          <RatingKey>
            <Box mb={6}>
              <Collapse in={open} collapsedSize={0} sx={{ my: 3 }}>
                <Grid container spacing={2}>
                  <Info item xs={12} md={6}>
                    <StarRating rating={2} starColor={values[2]} />
                    <Typography variant='body2'>
                      Exceeds guests minimum requirements
                    </Typography>

                    <StarRating rating={3} starColor={values[2]} />
                    <Typography variant='body2'>
                      A good level of quality &amp; facilities
                    </Typography>

                    <StarRating rating={4} starColor={values[2]} />
                    <Typography variant='body2'>
                      Consistently high levels of quality & facilities
                    </Typography>

                    <StarRating rating={5} starColor={values[2]} />
                    <Typography variant='body2'>
                      Exceptional quality throughout
                    </Typography>
                  </Info>
                  <Key item xs={12} md={6}>
                    <IconWrapper
                      name='star'
                      className='orange'
                      width='1.3rem'
                    />
                    <Typography variant='body2'>
                      Orange - Standard level of quality within their star
                      rating
                    </Typography>

                    <IconWrapper
                      name='star'
                      className='silver'
                      width='1.3rem'
                    />
                    <Typography variant='body2'>
                      Silver - Superior level of quality within their star
                      rating
                    </Typography>

                    <IconWrapper name='star' className='gold' width='1.3rem' />
                    <Typography variant='body2'>
                      Gold - Supreme level of quality within their star rating
                    </Typography>
                  </Key>
                </Grid>
              </Collapse>
            </Box>
          </RatingKey>
        </React.Fragment>
      );
    });

    return renderImages;
  }

  return (
    <Wrapper>
      {attrs['Quality In Tourism Grading'] && (
        <RatingImageArea>{veRating(attrs)}</RatingImageArea>
      )}
      {/* Conditionally render 'Clean, Safe & Legal' image outside of grading map */}
      {attrs['Clean, Safe & Legal'] && (
        <DatedImage hasQitg={attrs['Quality In Tourism Grading']}>
          <Grid item xs={6} md={3}>
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              mb={1}
            >
              <img className='qtg' alt='Quality in tourism logo' src={qt} />
            </Box>
          </Grid>
        </DatedImage>
      )}
    </Wrapper>
  );
};

export default PropertyGrading;
